import { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment-timezone';
import SearchableSelect from 'components/Forms/searchable/SearchableSelect';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  TextField,
  Switch,
  MenuItem,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Breadcrumbs,
  Checkbox,
  Radio,
  FormControlLabel,
  FormControl,
  InputLabel,
  ListItemText,
  Autocomplete,
  Chip,
  OutlinedInput,
  InputAdornment,
  CircularProgress,
  IconButton,
} from '@mui/material';
import { priceTransform, formatNumber } from '../../../helpers/formPrice';
import {
  Save as SaveIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  ArrowBack as ArrowBackIcon,
  NavigateNext as NavigateNextIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import confirm from '../../../components/Confirm';
import TranslationInput from '../../../components/PosMenu/TranslationInput';
import FieldSavingIndicator from '../../../components/PosMenu/FieldSavingIndicator';
import useScheduledTimeouts from '../hooks/useScheduledTimeouts';
import PosProviderPriceOverridePopover from '../../../components/PosMenu/PosProviderPriceOverridePopover';
import ModifierGroupManager from './ModifierGroupManager';
import ItemInventory from './ItemInventory';
import ImageUpload from '../../../components/FormFields/ImageUpload';
import {
  createItem,
  updateItem,
  deleteItem,
  clearSelected,
  goBack,
  goToHistoryIndex,
  setSelectedEntity,
} from '../../../redux/actions/posMenu';
import PromotionTab from '../../PosSettings/Promotions/PromotionTab';
import usePromotionsEnabled from 'hooks/usePromotionsEnabled';
import {
  DEFAULT_ITEM,
  SOLD_OUT_STATUS,
  getTranslationValue,
  getSoldOutDate,
  PRICE_TYPE_OPTIONS,
  IN_ORDER_VIEW_LAYOUT_OPTIONS,
} from '../../../constants/posMenu';
import { filteringSoldOut } from '../../../utils/soldOut';
import { getPrinterTags } from '../../../redux/actions/printers';
import { selectPrinterTagsList } from '../../../redux/selectors';

// Recursive component to render nested modifiers at any depth
// Now stores all preselected options on the parent item only using composite keys
const NestedModifierSelector = ({
  optionItem,
  menuMap,
  depth = 1,
  expandedNestedOptions,
  setExpandedNestedOptions,
  parentKey,
  preselectSaving,
  setPreselectSaving,
  parentPreselectOptions,
  onNestedPreselectChange,
}) => {
  if (!optionItem?.modifierGroupIds?.length) return null;

  const handleNestedOptionToggle = (
    compositeKey,
    nestedOptionId,
    isRadio = false
  ) => {
    // Block if any preselect save is in progress
    if (preselectSaving) return;

    const currentNestedOptions =
      parentPreselectOptions?.options?.[compositeKey] || [];

    let newNestedSelectedOptions;
    const existingNestedOption = currentNestedOptions.find(
      (opt) => opt._id === nestedOptionId
    );

    if (isRadio) {
      newNestedSelectedOptions = existingNestedOption
        ? []
        : [{ _id: nestedOptionId, count: 1, sticky: false }];
    } else {
      if (existingNestedOption) {
        newNestedSelectedOptions = currentNestedOptions.filter(
          (opt) => opt._id !== nestedOptionId
        );
      } else {
        newNestedSelectedOptions = [
          ...currentNestedOptions,
          { _id: nestedOptionId, count: 1, sticky: false },
        ];
      }
    }

    // Update parent's preselectOptions using callback
    onNestedPreselectChange(compositeKey, newNestedSelectedOptions);
  };

  const handleNestedQuantityChange = (
    compositeKey,
    nestedOptionId,
    count
  ) => {
    // Block if any preselect save is in progress
    if (preselectSaving) return;

    const currentNestedOptions =
      parentPreselectOptions?.options?.[compositeKey] || [];

    let newNestedSelectedOptions;

    if (count === 0 || count === '') {
      newNestedSelectedOptions = currentNestedOptions.filter(
        (opt) => opt._id !== nestedOptionId
      );
    } else {
      const existingNestedOption = currentNestedOptions.find(
        (opt) => opt._id === nestedOptionId
      );
      if (existingNestedOption) {
        newNestedSelectedOptions = currentNestedOptions.map((opt) =>
          opt._id === nestedOptionId ? { ...opt, count } : opt
        );
      } else {
        newNestedSelectedOptions = [
          ...currentNestedOptions,
          { _id: nestedOptionId, count, sticky: false },
        ];
      }
    }

    // Update parent's preselectOptions using callback
    onNestedPreselectChange(compositeKey, newNestedSelectedOptions);
  };

  return (
    <Box
      sx={{
        pl: 3,
        py: 1,
        borderLeft: `2px solid ${
          depth === 1 ? '#1976d2' : depth === 2 ? '#4caf50' : '#ff9800'
        }`,
        ml: 2,
        bgcolor: depth === 1 ? '#f5f9ff' : depth === 2 ? '#f5fff5' : '#fffaf5',
      }}
    >
      {optionItem.modifierGroupIds.map((nestedModGroupId) => {
        const nestedModGroup = menuMap?.modifierGroups?.[nestedModGroupId];
        if (!nestedModGroup) return null;

        const nestedOptions = nestedModGroup.optionIds
          ?.map((nestedOptId) => {
            const nestedOption = nestedModGroup.options?.find(
              (opt) => opt.itemId === nestedOptId
            );
            const nestedItemData = menuMap?.items?.[nestedOptId];
            return nestedOption
              ? { ...nestedOption, itemData: nestedItemData }
              : nestedItemData
              ? { itemId: nestedOptId, itemData: nestedItemData }
              : null;
          })
          .filter(Boolean);

        if (!nestedOptions || nestedOptions.length === 0) return null;

        // Path-based composite key encodes the full tree path for uniqueness
        const compositeKey = `${parentKey}_${nestedModGroupId}`;
        const legacyKey = `${optionItem._id}_${nestedModGroupId}`;
        const nestedSelectedOptions =
          parentPreselectOptions?.options?.[compositeKey] ||
          parentPreselectOptions?.options?.[legacyKey] ||
          [];
        const isNestedRadioMode =
          nestedModGroup.min === 1 && nestedModGroup.max === 1;
        const showNestedQuantityInput =
          !isNestedRadioMode && nestedModGroup.canSelectMultipleOptions;

        return (
          <Box key={nestedModGroupId} sx={{ mb: 2 }}>
            <Typography
              sx={{
                fontSize: '12px',
                fontWeight: 600,
                mb: 1,
                textTransform: 'uppercase',
                color:
                  depth === 1 ? '#1976d2' : depth === 2 ? '#4caf50' : '#ff9800',
              }}
            >
              {getTranslationValue(nestedModGroup.name)}
            </Typography>

            {nestedOptions.map((nestedOpt) => {
              const nestedOptId = nestedOpt.itemId;
              const isNestedSelected = nestedSelectedOptions.some(
                (opt) => opt._id === nestedOptId
              );
              const nestedSelectedOpt = nestedSelectedOptions.find(
                (opt) => opt._id === nestedOptId
              );
              const nestedOptName =
                getTranslationValue(nestedOpt.itemData?.name) || 'Option';

              // Check if this nested option has its own modifier groups
              const hasDeepNestedModifiers =
                nestedOpt.itemData?.modifierGroupIds?.length > 0;
              const expandKey = `${parentKey}_${nestedModGroupId}_${nestedOptId}`;
              const isDeepExpanded = expandedNestedOptions[expandKey];

              return (
                <Box key={nestedOptId}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      py: 0.5,
                      opacity: preselectSaving ? 0.6 : 1,
                    }}
                  >
                    {isNestedRadioMode ? (
                      <FormControlLabel
                        control={
                          <Radio
                            checked={isNestedSelected}
                            disabled={preselectSaving}
                            onClick={() =>
                              handleNestedOptionToggle(
                                compositeKey,
                                nestedOptId,
                                true
                              )
                            }
                            size="small"
                          />
                        }
                        label={
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                            }}
                          >
                            <Typography sx={{ fontSize: '13px' }}>
                              {nestedOptName}
                            </Typography>
                            {preselectSaving && <CircularProgress size={10} />}
                          </Box>
                        }
                      />
                    ) : showNestedQuantityInput ? (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          flex: 1,
                        }}
                      >
                        <Typography sx={{ fontSize: '13px', flex: 1 }}>
                          {nestedOptName}
                        </Typography>
                        <TextField
                          size="small"
                          type="number"
                          label="Qty"
                          value={nestedSelectedOpt?.count || ''}
                          disabled={preselectSaving}
                          onChange={(e) => {
                            const val = e.target.value;
                            handleNestedQuantityChange(
                              compositeKey,
                              nestedOptId,
                              val === '' ? '' : parseInt(val)
                            );
                          }}
                          onWheel={(e) => e.target.blur()}
                          inputProps={{ min: 0 }}
                          sx={{ width: 70 }}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Box>
                    ) : (
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={isNestedSelected}
                            disabled={preselectSaving}
                            onChange={() =>
                              handleNestedOptionToggle(
                                compositeKey,
                                nestedOptId,
                                false
                              )
                            }
                            size="small"
                          />
                        }
                        label={
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                            }}
                          >
                            <Typography sx={{ fontSize: '13px' }}>
                              {nestedOptName}
                            </Typography>
                            {preselectSaving && <CircularProgress size={10} />}
                          </Box>
                        }
                      />
                    )}

                    {/* Expand button for deeper nested modifiers */}
                    <Box
                      sx={{
                        width: 32,
                        minWidth: 32,
                        display: 'flex',
                        justifyContent: 'center',
                      }}
                    >
                      {hasDeepNestedModifiers && (
                        <IconButton
                          size="small"
                          onClick={() =>
                            setExpandedNestedOptions((prev) => ({
                              ...prev,
                              [expandKey]: !prev[expandKey],
                            }))
                          }
                        >
                          {isDeepExpanded ? (
                            <ExpandLessIcon fontSize="small" />
                          ) : (
                            <ExpandMoreIcon fontSize="small" />
                          )}
                        </IconButton>
                      )}
                    </Box>
                  </Box>

                  {/* Recursively render deeper nested modifiers */}
                  {hasDeepNestedModifiers && isDeepExpanded && (
                    <NestedModifierSelector
                      optionItem={nestedOpt.itemData}
                      menuMap={menuMap}
                      depth={depth + 1}
                      expandedNestedOptions={expandedNestedOptions}
                      setExpandedNestedOptions={setExpandedNestedOptions}
                      parentKey={expandKey}
                      preselectSaving={preselectSaving}
                      setPreselectSaving={setPreselectSaving}
                      parentPreselectOptions={parentPreselectOptions}
                      onNestedPreselectChange={onNestedPreselectChange}
                    />
                  )}
                </Box>
              );
            })}
          </Box>
        );
      })}
    </Box>
  );
};

const ItemEditor = ({ item, menuMap }) => {
  const dispatch = useDispatch();
  const {
    navigationHistory,
    internalMenuId,
    selectedEntity,
    selectedEntityType,
    currentBusiness,
  } = useSelector((state) => ({
    navigationHistory: state.posMenu.navigationHistory,
    internalMenuId: state.posMenu.internalMenuId,
    selectedEntity: state.posMenu.selectedEntity,
    selectedEntityType: state.posMenu.selectedEntityType,
    currentBusiness: state.business?.currentBusiness,
  }));
  const printerTagsList = useSelector(selectPrinterTagsList);
  const businessTimezone = currentBusiness?.timezone?.timeZoneId;
  const [formData, setFormData] = useState(DEFAULT_ITEM);
  const [isSaving, setIsSaving] = useState(false);
  const [fieldSavingStatus, setFieldSavingStatus] = useState({});
  const [expandedNestedOptions, setExpandedNestedOptions] = useState({});
  const [isInventoryExpanded, setIsInventoryExpanded] = useState(false);
  const [preselectSaving, setPreselectSaving] = useState(false);
  const preselectSavingRef = useRef(false); // Synchronous ref for immediate check
  const saveTimeoutsRef = useRef({});
  const scheduleTimeout = useScheduledTimeouts();

  // Only show back button if there's meaningful navigation history (more than 1 item)
  // This excludes the case where user just clicked from tree
  const hasHistory = navigationHistory && navigationHistory.length > 0;
  const isEditing = item && item._id;
  const promotionsEnabled = usePromotionsEnabled();

  // Helper function to handle numeric input validation
  const handleNumericKeyDown = (e, allowDecimal = false) => {
    // Allow: backspace, delete, tab, escape, enter
    const allowedKeys = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter'];
    if (allowDecimal) {
      allowedKeys.push('.');
    }
    if (
      allowedKeys.includes(e.key) ||
      // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
      (e.ctrlKey && ['a', 'c', 'v', 'x'].includes(e.key.toLowerCase())) ||
      // Allow: home, end, left, right
      ['Home', 'End', 'ArrowLeft', 'ArrowRight'].includes(e.key)
    ) {
      return;
    }
    // Block if not a number
    if (isNaN(Number(e.key))) {
      e.preventDefault();
    }
  };

  // Get the fresh item from menuMap to ensure we have the latest data (e.g., modifierGroupIds)
  const currentItem =
    isEditing && menuMap?.items?.[item._id] ? menuMap.items[item._id] : item;

  // Collapse the inventory section whenever a different item is selected so it
  // never shows the previously selected item's inventory data.
  useEffect(() => {
    setIsInventoryExpanded(false);
  }, [item?._id]);

  // Track the last initialized item ID to prevent re-initialization on menuMap changes
  const lastInitializedItemIdRef = useRef(null);

  useEffect(() => {
    // Only initialize form when item ID changes, not when menuMap updates
    const currentItemId = item?._id || null;
    // For existing items, skip re-initialization if same item
    // For new items (null ID), allow re-initialization when menuMap loads
    if (currentItemId && currentItemId === lastInitializedItemIdRef.current) {
      return; // Skip re-initialization if same existing item
    }
    lastInitializedItemIdRef.current = currentItemId;

    if (item && item._id) {
      // Get the fresh item data from menuMap if available
      const freshItem = menuMap?.items?.[item._id] || item;

      // Editing existing item - find category from menuMap
      let categoryId = freshItem.category || freshItem.categoryId || '';

      // If still no category, search for it in menuMap categories
      if (!categoryId && menuMap?.categories) {
        // Find which category contains this item
        const categoryEntry = Object.values(menuMap.categories).find(
          (cat) => cat.itemIds && cat.itemIds.includes(freshItem._id)
        );
        if (categoryEntry) {
          categoryId = categoryEntry._id;
        }
      }

      setFormData({
        ...freshItem,
        category: categoryId,
        tax: freshItem.tax === 0 ? 0 : freshItem.tax || null, // Preserve 0 as 0, but treat undefined as null
        preselectOptions: freshItem.preselectOptions || {
          defaultSelection: false,
          options: {},
        },
      });
    } else {
      // New item - check for category from multiple sources
      let categoryId = null;

      // First, check if item has categoryId (from MenuTree handleAddItem)
      if (item && item.categoryId) {
        categoryId = item.categoryId;
      }
      // Second, check if there's a currently selected category
      else if (selectedEntityType === 'category' && selectedEntity?._id) {
        categoryId = selectedEntity._id;
      }
      // Third, check navigation history for last selected category
      else if (navigationHistory && navigationHistory.length > 0) {
        for (let i = navigationHistory.length - 1; i >= 0; i--) {
          const historyItem = navigationHistory[i];
          if (
            historyItem.entityType === 'category' &&
            historyItem.entity?._id
          ) {
            categoryId = historyItem.entity._id;
            break;
          }
        }
      }

      setFormData({ ...DEFAULT_ITEM, category: categoryId || '' });
    }
  }, [item, menuMap, navigationHistory, selectedEntity, selectedEntityType]);

  // Sync preselectOptions from Redux when changed externally (e.g., by ModifierGroupOptionsManager)
  // Use a ref to track the last synced value to avoid infinite loops
  const lastSyncedPreselectOptionsRef = useRef(null);

  useEffect(() => {
    if (!item?._id) return;
    const freshItem = menuMap?.items?.[item._id];
    if (!freshItem) return;

    const reduxPreselectOptions = freshItem.preselectOptions;
    const reduxStr = JSON.stringify(reduxPreselectOptions);

    // Only sync if Redux has different data than what we last synced
    // This prevents infinite loops when formData.preselectOptions changes
    if (reduxStr !== lastSyncedPreselectOptionsRef.current) {
      lastSyncedPreselectOptionsRef.current = reduxStr;
      setFormData((prev) => ({
        ...prev,
        preselectOptions: reduxPreselectOptions || {
          defaultSelection: false,
          options: {},
        },
      }));
    }
  }, [item?._id, menuMap?.items]);

  useEffect(() => {
    return () => {
      Object.values(saveTimeoutsRef.current).forEach((pending) => {
        if (pending?.timeoutId) clearTimeout(pending.timeoutId);
        if (pending?.saveCallback) pending.saveCallback();
      });
      saveTimeoutsRef.current = {};
    };
  }, [item?._id]);

  // Fetch printer tags
  useEffect(() => {
    dispatch(getPrinterTags());
  }, [dispatch]);

  // Helper function to clean preselectOptions by removing stale modifier groups and options
  // Preserves nested preselection composite keys (format: optionItemId_nestedModGroupId)
  const cleanPreselectOptions = (currentOptions) => {
    if (!currentOptions || !item?.modifierGroupIds) {
      return {};
    }

    const cleanedOptions = {};
    const validModifierGroupIds = new Set(item.modifierGroupIds);

    for (const [key, selections] of Object.entries(currentOptions)) {
      // Check if this is a composite key for nested preselections (contains underscore)
      // Composite keys have format: optionItemId_nestedModGroupId
      const isCompositeKey = key.includes('_');

      if (isCompositeKey) {
        // For composite keys (nested selections), preserve them as-is
        // These are managed by the NestedModifierSelector component
        if (selections && selections.length > 0) {
          cleanedOptions[key] = selections;
        }
        continue;
      }

      // For top-level modifier group keys, validate against item's modifier groups
      if (!validModifierGroupIds.has(key)) {
        continue;
      }

      const modGroup = menuMap?.modifierGroups?.[key];
      if (!modGroup) {
        continue;
      }

      // Get valid option IDs for this modifier group
      const validOptionIds = new Set(modGroup.optionIds || []);

      // Filter out options that are no longer in this modifier group
      const cleanedSelections = (selections || []).filter((sel) =>
        validOptionIds.has(sel._id)
      );

      // Only keep the modifier group entry if it has valid selections
      if (cleanedSelections.length > 0) {
        cleanedOptions[key] = cleanedSelections;
      }
    }

    return cleanedOptions;
  };

  const handleSave = async () => {
    try {
      if (!formData.name?.en?.trim()) {
        toast.error('Name is Required');
        return;
      }

      setIsSaving(true);

      if (item && item._id) {
        // Add provider field for updates only
        const dataToSave = {
          ...formData,
          provider: 'pos',
        };
        await dispatch(updateItem(item._id, dataToSave));
        toast.success('Item updated successfully');

        // If we have navigation history, go back to previous screen after update
        if (hasHistory) {
          dispatch(goBack());
        }
      } else {
        // Validate that internalMenuId is available for new items
        if (!internalMenuId) {
          toast.error(
            'Menu information is not loaded. Please refresh the page.'
          );
          return;
        }
        // Don't include provider field for creates, but add internal menu ID
        // Remove empty category field if not set
        const dataToCreate = {
          ...formData,
          menu: internalMenuId,
          businessUuid: currentBusiness?.uuid,
        };
        if (!dataToCreate.category) {
          delete dataToCreate.category;
        }
        // Ensure soldOut has the correct format for backend (status and soldOutDate)
        if (dataToCreate.soldOut) {
          const status =
            dataToCreate.soldOut.status || SOLD_OUT_STATUS.AVAILABLE;
          dataToCreate.soldOut = {
            status: status,
            soldOutDate: getSoldOutDate(status, businessTimezone, moment),
          };
        }
        const createdItem = await dispatch(createItem(dataToCreate));
        toast.success('Item created successfully');

        // After creating, select the newly created item to show it in editor
        dispatch(setSelectedEntity(createdItem, 'item', true));
      }
    } catch (error) {
      toast.error(error.message || 'An error occurred');
    } finally {
      setIsSaving(false);
    }
  };

  const handleBack = () => {
    dispatch(goBack());
  };

  const handleDeleteClick = async () => {
    if (!item || !item._id) return;

    const confirmed = await confirm({
      title: 'Confirm Delete',
      message: `Are you sure you want to delete "${
        formData.name?.en || 'this item'
      }"? This action cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      confirmColor: 'error',
    });

    if (!confirmed) return;

    try {
      await dispatch(deleteItem(item._id));

      // Navigate/clear selection and show success
      if (hasHistory) {
        dispatch(goBack());
      } else {
        dispatch(clearSelected());
      }

      toast.success('Item deleted successfully');
    } catch (error) {
      toast.error(error.message || 'An error occurred');
    }
  };

  const isFieldValueEqual = (newValue, storedValue) => {
    if (newValue === storedValue) return true;
    if (newValue == null && storedValue == null) return true;
    if (newValue == null || storedValue == null) return false;
    if (
      typeof newValue === 'object' &&
      'en' in newValue &&
      typeof storedValue === 'object'
    ) {
      return (newValue.en || '') === (storedValue.en || '');
    }
    return JSON.stringify(newValue) === JSON.stringify(storedValue);
  };

  // Auto-save handler for existing items
  const handleFieldAutoSave = async (fieldName, value) => {
    if (!isEditing) return;

    // Cancel the pending save before the unchanged check, otherwise toggling a
    // value back within the debounce window would still send the stale value
    const pendingSave = saveTimeoutsRef.current[fieldName];
    if (pendingSave?.timeoutId) {
      clearTimeout(pendingSave.timeoutId);
      saveTimeoutsRef.current[fieldName] = null;
    }

    // Skip save if value hasn't changed from the stored value
    const storedItem = menuMap?.items?.[item._id];
    // An edited price is a string ("6.59"), the stored one a number
    const isUnchanged =
      fieldName === 'price'
        ? Number(value) === Number(storedItem?.price)
        : isFieldValueEqual(value, storedItem?.[fieldName]);
    if (storedItem && isUnchanged) {
      if (pendingSave) {
        setFieldSavingStatus((prev) => ({ ...prev, [fieldName]: 'idle' }));
      }
      return;
    }

    // For preselectOptions, use synchronous ref check + state for UI
    if (fieldName === 'preselectOptions') {
      // If already saving (synchronous check), just update formData but don't trigger another save
      if (preselectSavingRef.current) {
        setFormData((prev) => ({ ...prev, preselectOptions: value }));
        return;
      }
      // Set saving state immediately (both ref and state)
      preselectSavingRef.current = true;
      setPreselectSaving(true);
    }

    // Set saving status
    setFieldSavingStatus((prev) => ({ ...prev, [fieldName]: 'saving' }));

    // For image field, save immediately (no debounce) since upload already took time
    // For preselectOptions, use shorter debounce for responsiveness
    // For other fields, debounce by 300ms
    const debounceTime =
      fieldName === 'image' ? 0 : fieldName === 'preselectOptions' ? 100 : 300;

    // Create the save callback that can be called immediately on unmount or after debounce
    const saveCallback = async () => {
      try {
        // Validation
        if (fieldName === 'name' && !value?.en?.trim()) {
          setFieldSavingStatus((prev) => ({ ...prev, [fieldName]: 'error' }));
          if (fieldName === 'preselectOptions') {
            preselectSavingRef.current = false;
            setPreselectSaving(false);
          }
          toast.error('Name is Required');
          return;
        }

        // Prepare update data
        const updateData = {
          [fieldName]: value,
          provider: 'pos',
        };

        // Send update
        await dispatch(updateItem(item._id, updateData));

        setFormData((prev) => {
          if (prev._id !== item._id) return prev;
          return { ...prev, [fieldName]: value };
        });

        // Show success status
        setFieldSavingStatus((prev) => ({ ...prev, [fieldName]: 'saved' }));

        // Clear success indicator after 2 seconds
        scheduleTimeout(() => {
          setFieldSavingStatus((prev) => ({ ...prev, [fieldName]: 'idle' }));
        }, 2000);
      } catch (error) {
        setFieldSavingStatus((prev) => ({ ...prev, [fieldName]: 'error' }));
        toast.error(`Failed to save ${fieldName}: ${error.message}`);
      } finally {
        // Clear preselectOptions saving state (both ref and state)
        if (fieldName === 'preselectOptions') {
          preselectSavingRef.current = false;
          setPreselectSaving(false);
        }
      }
    };

    // Debounce the save. Once it fires it is no longer pending, so an unmount
    // during the request must not send it again.
    const timeoutId = setTimeout(() => {
      saveTimeoutsRef.current[fieldName] = null;
      saveCallback();
    }, debounceTime);

    // Store both timeout ID and save callback in ref
    saveTimeoutsRef.current[fieldName] = { timeoutId, saveCallback };
  };
  // Convert categories object to array and sort by sort order, then by name
  const categories = menuMap?.categories
    ? Object.values(menuMap.categories).sort((a, b) => {
        const sortDiff = (a.sort || 0) - (b.sort || 0);
        if (sortDiff !== 0) return sortDiff;
        // Secondary sort by name when sort values are equal
        const nameA = getTranslationValue(a.name).toLowerCase();
        const nameB = getTranslationValue(b.name).toLowerCase();
        return nameA.localeCompare(nameB);
      })
    : [];

  // Build breadcrumb items from navigation history
  const buildBreadcrumbItems = () => {
    const items = [];

    // Add items from navigation history
    navigationHistory.forEach((historyItem, index) => {
      const { entity, entityType } = historyItem;
      let name = '';

      if (entityType === 'category') {
        name = entity?.name?.en || 'Category';
      } else if (entityType === 'item') {
        name = entity?.name?.en || 'Item';
      } else if (entityType === 'modifierGroup') {
        name = entity?.name?.en || 'Modifier Group';
      }

      items.push({
        name,
        entityType,
        index,
      });
    });

    // Add current item
    if (item) {
      items.push({
        name: item?.name?.en || 'Current Item',
        entityType: 'item',
        isCurrent: true,
      });
    }

    return items;
  };

  const breadcrumbItems = buildBreadcrumbItems();

  return (
    <Box>
      {/* Breadcrumb navigation */}
      {breadcrumbItems.length > 0 && (
        <Box sx={{ mb: 1 }}>
          <Breadcrumbs
            separator={<NavigateNextIcon fontSize="small" />}
            sx={{ fontSize: '13px' }}
          >
            {breadcrumbItems.map((crumb, idx) => (
              <Typography
                key={idx}
                onClick={() => {
                  if (!crumb.isCurrent && typeof crumb.index === 'number') {
                    dispatch(goToHistoryIndex(crumb.index));
                  }
                }}
                sx={{
                  fontSize: '13px',
                  color: crumb.isCurrent ? 'text.primary' : 'text.secondary',
                  fontWeight: crumb.isCurrent ? 500 : 400,
                  cursor: crumb.isCurrent ? 'default' : 'pointer',
                  '&:hover': !crumb.isCurrent
                    ? {
                        textDecoration: 'underline',
                      }
                    : {},
                }}
              >
                {crumb.name}
              </Typography>
            ))}
          </Breadcrumbs>
        </Box>
      )}

      {/* Back button - only shown when navigating between items */}
      {hasHistory && (
        <Box sx={{ mb: 2 }}>
          <Button
            variant="text"
            size="small"
            startIcon={<ArrowBackIcon fontSize="small" />}
            onClick={handleBack}
            sx={{
              color: 'text.secondary',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            Back
          </Button>
        </Box>
      )}

      <Box
        sx={{
          mb: 3,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontSize: '20px',
            fontWeight: 600,
            color: '#141414',
          }}
        >
          {isEditing ? 'Edit Item' : 'New Item'}
        </Typography>
        <Box>
          {isEditing && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleDeleteClick}
              sx={{ mr: 1 }}
            >
              Delete
            </Button>
          )}
          {!isEditing && (
            <Button
              variant="contained"
              color="primary"
              startIcon={
                isSaving ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <SaveIcon />
                )
              }
              onClick={handleSave}
              disabled={isSaving}
            >
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
          )}
        </Box>
      </Box>

      {/* Basic Information */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Basic Information
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Box
            sx={{ mb: 2, display: 'flex', alignItems: 'flex-start', gap: 2 }}
          >
            <Box sx={{ flex: 1 }}>
              <TranslationInput
                label="Item Name"
                value={formData.name || { en: '', es: '' }}
                onChange={(value) => setFormData({ ...formData, name: value })}
                onBlur={(value) => handleFieldAutoSave('name', value)}
                required
              />
            </Box>
            <Box sx={{ minWidth: 24, mt: 1 }}>
              <FieldSavingIndicator status={fieldSavingStatus.name} />
            </Box>
          </Box>

          <Box
            sx={{ mb: 2, display: 'flex', alignItems: 'flex-start', gap: 2 }}
          >
            <Box sx={{ flex: 1 }}>
              <TranslationInput
                label="Internal Name"
                value={formData.title || { en: '', es: '' }}
                onChange={(value) => setFormData({ ...formData, title: value })}
                onBlur={(value) => handleFieldAutoSave('title', value)}
              />
            </Box>
            <Box sx={{ minWidth: 24, mt: 1 }}>
              <FieldSavingIndicator status={fieldSavingStatus.title} />
            </Box>
          </Box>

          <Box
            sx={{ mb: 2, display: 'flex', alignItems: 'flex-start', gap: 2 }}
          >
            <Box sx={{ flex: 1 }}>
              <TranslationInput
                label="Description"
                value={formData.description || { en: '', es: '' }}
                onChange={(value) =>
                  setFormData({ ...formData, description: value })
                }
                onBlur={(value) => handleFieldAutoSave('description', value)}
                multiline
                rows={4}
              />
            </Box>
            <Box sx={{ minWidth: 24, mt: 1 }}>
              <FieldSavingIndicator status={fieldSavingStatus.description} />
            </Box>
          </Box>

          <Box
            sx={{ mb: 2, display: 'flex', alignItems: 'flex-start', gap: 2 }}
          >
            <Autocomplete
              fullWidth
              size="small"
              options={[
                { _id: '', name: { en: 'No Category' } },
                ...categories,
              ]}
              getOptionLabel={(option) =>
                option?.name?.en || 'Unnamed Category'
              }
              value={
                formData.category
                  ? categories.find((cat) => cat._id === formData.category) ||
                    null
                  : { _id: '', name: { en: 'No Category' } }
              }
              onChange={(_, newValue) => {
                const newCategoryId = newValue?._id || '';
                setFormData({ ...formData, category: newCategoryId });
                if (isEditing) {
                  handleFieldAutoSave('category', newCategoryId);
                }
              }}
              isOptionEqualToValue={(option, value) =>
                option?._id === value?._id
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Category"
                  sx={{
                    '& .MuiInputBase-root': {
                      fontSize: '14px',
                    },
                  }}
                />
              )}
              renderOption={(props, option) => (
                <li {...props} key={option._id || 'no-category'}>
                  <Typography sx={{ fontSize: '14px' }}>
                    {option?.name?.en || 'Unnamed Category'}
                  </Typography>
                </li>
              )}
            />
            <Box sx={{ minWidth: 24, mt: 1 }}>
              <FieldSavingIndicator status={fieldSavingStatus.category} />
            </Box>
          </Box>

          <Box
            sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'flex-start' }}
          >
            <TextField
              label="Price"
              type="text"
              value={formData.price != null ? formatNumber(formData.price) : ''}
              onChange={(e) => {
                const transformed = priceTransform(e.target.value);
                setFormData({
                  ...formData,
                  price: transformed,
                });
              }}
              onBlur={() => {
                handleFieldAutoSave('price', formData.price);
              }}
              fullWidth
              required
              InputLabelProps={{ shrink: true }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    {formData.priceType && formData.priceType !== 'fixPrice'
                      ? '×'
                      : '$'}
                  </InputAdornment>
                ),
                endAdornment: isEditing ? (
                  <InputAdornment position="end">
                    <PosProviderPriceOverridePopover
                      itemId={item._id}
                      availableInKiosk={item.availableInKiosk}
                    />
                  </InputAdornment>
                ) : null,
              }}
              sx={{
                flex: 1,
                '& .MuiInputBase-root': {
                  fontSize: '14px',
                },
              }}
            />
            <Box sx={{ minWidth: 24, mt: 1 }}>
              <FieldSavingIndicator status={fieldSavingStatus.price} />
            </Box>

            <TextField
              label="Tax (%)"
              type="number"
              value={
                formData.tax === null || formData.tax === undefined
                  ? ''
                  : formData.tax
              }
              onChange={(e) => {
                const value = e.target.value;
                // Only allow valid numeric values
                if (value === '' || !isNaN(parseFloat(value))) {
                  setFormData({
                    ...formData,
                    tax:
                      value === '' || value === null ? null : parseFloat(value),
                  });
                }
              }}
              onBlur={(e) => {
                const value = e.target.value;
                handleFieldAutoSave(
                  'tax',
                  value === '' || value === null ? null : parseFloat(value)
                );
              }}
              onKeyDown={(e) => handleNumericKeyDown(e, true)}
              onWheel={(e) => e.target.blur()}
              fullWidth
              InputLabelProps={{ shrink: true }}
              inputProps={{ step: '0.01', min: '0' }}
              sx={{
                flex: 1,
                '& .MuiInputBase-root': {
                  fontSize: '14px',
                },
              }}
            />
            <Box sx={{ minWidth: 24, mt: 1 }}>
              <FieldSavingIndicator status={fieldSavingStatus.tax} />
            </Box>
          </Box>

          <Box
            sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'flex-start' }}
          >
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel id="price-type-label">Price Type</InputLabel>
              <SearchableSelect
                labelId="price-type-label"
                label="Price Type"
                value={formData.priceType || ''}
                displayEmpty
                renderValue={(selected) => {
                  const option = PRICE_TYPE_OPTIONS.find(
                    (o) => o.value === selected
                  );
                  return option?.label || 'None';
                }}
                onChange={(e) => {
                  const newValue = e.target.value || null;
                  setFormData((prev) => ({ ...prev, priceType: newValue }));
                  if (isEditing) {
                    handleFieldAutoSave('priceType', newValue);
                  }
                }}
              >
                {PRICE_TYPE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </SearchableSelect>
            </FormControl>
            <FieldSavingIndicator status={fieldSavingStatus.priceType} />
          </Box>

          <Box
            sx={{
              mb: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <Typography
              sx={{
                fontSize: '14px',
                fontWeight: 500,
                minWidth: 100,
              }}
            >
              Popular Item
            </Typography>
            <Switch
              checked={formData.isPopular || false}
              onChange={(e) => {
                const newValue = e.target.checked;
                setFormData({ ...formData, isPopular: newValue });
                if (isEditing) {
                  handleFieldAutoSave('isPopular', newValue);
                }
              }}
            />
            <FieldSavingIndicator status={fieldSavingStatus.isPopular} />
          </Box>

          <Box
            sx={{
              mb: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel id="availability-select-label">
                Availability
              </InputLabel>
              <SearchableSelect
                labelId="availability-select-label"
                label="Availability"
                value={
                  filteringSoldOut(formData, currentBusiness) ||
                  SOLD_OUT_STATUS.AVAILABLE
                }
                onChange={(e) => {
                  const status = e.target.value;
                  const newSoldOut = {
                    status: status,
                    soldOutDate: getSoldOutDate(
                      status,
                      businessTimezone,
                      moment
                    ),
                  };
                  setFormData({
                    ...formData,
                    soldOut: newSoldOut,
                  });
                  if (isEditing) {
                    handleFieldAutoSave('soldOut', newSoldOut);
                  }
                }}
              >
                <MenuItem value={SOLD_OUT_STATUS.AVAILABLE}>
                  <ListItemText
                    disableTypography
                    primary={
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            bgcolor: '#4caf50',
                          }}
                        />
                        <Typography>Available</Typography>
                      </Box>
                    }
                  />
                </MenuItem>
                <MenuItem value={SOLD_OUT_STATUS.SOLD_OUT_TODAY}>
                  <ListItemText
                    disableTypography
                    primary={
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            bgcolor: '#ff9800',
                          }}
                        />
                        <Typography>Sold Out Today</Typography>
                      </Box>
                    }
                  />
                </MenuItem>
                <MenuItem value={SOLD_OUT_STATUS.SOLD_OUT_PERMANENT}>
                  <ListItemText
                    disableTypography
                    primary={
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            bgcolor: '#f44336',
                          }}
                        />
                        <Typography>Sold Out Permanently</Typography>
                      </Box>
                    }
                  />
                </MenuItem>
              </SearchableSelect>
            </FormControl>
            <FieldSavingIndicator status={fieldSavingStatus.soldOut} />
          </Box>

          <Divider sx={{ my: 3 }} />
        </CardContent>
      </Card>

      {/* Image Section */}
      <Card sx={{ mb: 3, boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)' }}>
        <CardContent sx={{ px: 4, py: 4 }}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{
              fontSize: '18px',
              fontWeight: 600,
              color: '#141414',
              mb: 1,
            }}
          >
            Product Image
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: '#9E9EAD',
              mb: 3,
            }}
          >
            Upload an image to display in your menu
          </Typography>
          <Divider sx={{ mb: 4 }} />
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            <ImageUpload
              name="image"
              initialValue={formData.image || { url: '', fileId: '' }}
              // Uploads finish late: update only the image, and only if the
              // form still shows the item the upload was started for
              setFormValue={(field, value) => {
                if (lastInitializedItemIdRef.current !== (item?._id || null)) {
                  return;
                }
                setFormData((prev) => ({ ...prev, [field]: value }));
                if (isEditing) {
                  handleFieldAutoSave(field, value);
                }
              }}
              setImage={(value) => {
                if (lastInitializedItemIdRef.current !== (item?._id || null)) {
                  return;
                }
                setFormData((prev) => ({ ...prev, image: value }));
                if (isEditing) {
                  handleFieldAutoSave('image', value);
                }
              }}
              removable={true}
              disabled={false}
              size={200}
              className="product-image"
              height="300"
              width="300"
            />
            <Box sx={{ minWidth: 24 }}>
              <FieldSavingIndicator status={fieldSavingStatus.image} />
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Modifiers Section */}
      {isEditing && (
        <Card sx={{ mb: 3, boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)' }}>
          <CardContent sx={{ px: 4, py: 4 }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{
                fontSize: '18px',
                fontWeight: 600,
                color: '#141414',
                mb: 1,
              }}
            >
              Modifier Groups
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: '#9E9EAD',
                mb: 3,
              }}
            >
              Manage customization options for this item
            </Typography>
            <Divider sx={{ mb: 4 }} />
            <ModifierGroupManager item={currentItem} menuMap={menuMap} />
          </CardContent>
        </Card>
      )}

      {/* Default Selections Accordion */}
      {isEditing && item?.modifierGroupIds?.length > 0 && (
        <Accordion
          sx={{
            mb: 3,
            boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
            '&:before': { display: 'none' },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{ px: 4, py: 2 }}
          >
            <Box>
              <Typography
                sx={{
                  fontSize: '18px',
                  fontWeight: 600,
                  color: '#141414',
                }}
              >
                Default Selections
              </Typography>
              <Typography
                sx={{
                  fontSize: '13px',
                  color: '#9E9EAD',
                  mt: 0.5,
                }}
              >
                Pre-select options for this item
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 4, pb: 4, pt: 2, position: 'relative' }}>
            {/* Loading overlay - disable entire section during save */}
            {preselectSaving && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  bgcolor: 'rgba(255, 255, 255, 0.7)',
                  zIndex: 10,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 1,
                }}
              >
                <CircularProgress size={32} />
              </Box>
            )}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 2,
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={
                      formData.preselectOptions?.defaultSelection || false
                    }
                    disabled={preselectSaving}
                    onChange={(e) => {
                      const cleanedOptions = cleanPreselectOptions(
                        formData.preselectOptions?.options
                      );
                      const newPreselectOptions = {
                        defaultSelection: e.target.checked,
                        options: cleanedOptions,
                      };
                      setFormData({
                        ...formData,
                        preselectOptions: newPreselectOptions,
                      });
                      if (isEditing) {
                        handleFieldAutoSave(
                          'preselectOptions',
                          newPreselectOptions
                        );
                      }
                    }}
                  />
                }
                label={
                  <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                    Enable Default Selections
                  </Typography>
                }
              />
              <FieldSavingIndicator
                status={fieldSavingStatus.preselectOptions}
              />
            </Box>

            {formData.preselectOptions?.defaultSelection && (
              <Box sx={{ pl: 2 }}>
                {item.modifierGroupIds.map((modGroupId) => {
                  const modGroup = menuMap?.modifierGroups?.[modGroupId];
                  if (!modGroup) return null;

                  const options = modGroup.optionIds
                    ?.map((optId) => {
                      const option = modGroup.options?.find(
                        (opt) => opt.itemId === optId
                      );
                      const itemData = menuMap?.items?.[optId];
                      return option
                        ? { ...option, itemData }
                        : itemData
                        ? { itemId: optId, itemData }
                        : null;
                    })
                    .filter(Boolean);

                  if (!options || options.length === 0) return null;

                  const selectedOptions =
                    formData.preselectOptions?.options?.[modGroupId] || [];

                  const handleOptionToggle = (optionId, isRadio = false) => {
                    let newSelectedOptions;
                    const existingOption = selectedOptions.find(
                      (opt) => opt._id === optionId
                    );

                    if (isRadio) {
                      // Radio: only one option can be selected
                      newSelectedOptions = existingOption
                        ? []
                        : [{ _id: optionId, count: 1, sticky: false }];
                    } else {
                      // Checkbox: toggle
                      if (existingOption) {
                        newSelectedOptions = selectedOptions.filter(
                          (opt) => opt._id !== optionId
                        );
                      } else {
                        newSelectedOptions = [
                          ...selectedOptions,
                          { _id: optionId, count: 1, sticky: false },
                        ];
                      }
                    }

                    const cleanedOptions = cleanPreselectOptions(
                      formData.preselectOptions?.options
                    );
                    const newPreselectOptions = {
                      defaultSelection:
                        formData.preselectOptions?.defaultSelection || false,
                      options: {
                        ...cleanedOptions,
                        [modGroupId]: newSelectedOptions,
                      },
                    };
                    setFormData({
                      ...formData,
                      preselectOptions: newPreselectOptions,
                    });
                    if (isEditing) {
                      handleFieldAutoSave(
                        'preselectOptions',
                        newPreselectOptions
                      );
                    }
                  };

                  const handleQuantityChange = (optionId, count) => {
                    const newSelectedOptions = selectedOptions.map((opt) =>
                      opt._id === optionId ? { ...opt, count } : opt
                    );
                    const cleanedOptions = cleanPreselectOptions(
                      formData.preselectOptions?.options
                    );
                    const newPreselectOptions = {
                      defaultSelection:
                        formData.preselectOptions?.defaultSelection || false,
                      options: {
                        ...cleanedOptions,
                        [modGroupId]: newSelectedOptions,
                      },
                    };
                    setFormData({
                      ...formData,
                      preselectOptions: newPreselectOptions,
                    });
                    if (isEditing) {
                      handleFieldAutoSave(
                        'preselectOptions',
                        newPreselectOptions
                      );
                    }
                  };

                  const handleStickyToggle = () => {
                    const currentSticky = selectedOptions.some(
                      (opt) => opt.sticky
                    );
                    const newSelectedOptions = selectedOptions.map((opt) => ({
                      ...opt,
                      sticky: !currentSticky,
                    }));
                    const cleanedOptions = cleanPreselectOptions(
                      formData.preselectOptions?.options
                    );
                    const newPreselectOptions = {
                      defaultSelection:
                        formData.preselectOptions?.defaultSelection || false,
                      options: {
                        ...cleanedOptions,
                        [modGroupId]: newSelectedOptions,
                      },
                    };
                    setFormData({
                      ...formData,
                      preselectOptions: newPreselectOptions,
                    });
                    if (isEditing) {
                      handleFieldAutoSave(
                        'preselectOptions',
                        newPreselectOptions
                      );
                    }
                  };

                  // Determine which control to show:
                  // 1. min=1 && max=1 -> Radio (single select, must pick exactly 1)
                  // 2. canSelectMultipleOptions=true -> Quantity TextField
                  // 3. Default -> Checkbox (multi-select with count=1)
                  const isRadioMode = modGroup.min === 1 && modGroup.max === 1;
                  const showQuantityInput =
                    !isRadioMode && modGroup.canSelectMultipleOptions;
                  const isStickyEnabled = selectedOptions.some(
                    (opt) => opt.sticky
                  );

                  // Handler for radio button (single select)
                  const handleRadioSelect = (optionId) => {
                    // Radio mode: only one option can be selected, clicking again deselects
                    const existingOption = selectedOptions.find(
                      (opt) => opt._id === optionId
                    );
                    let newSelectedOptions;
                    if (existingOption) {
                      // Deselect if clicking the same option
                      newSelectedOptions = [];
                    } else {
                      // Select this option, replacing any previous selection
                      newSelectedOptions = [
                        { _id: optionId, count: 1, sticky: false },
                      ];
                    }
                    const cleanedOptions = cleanPreselectOptions(
                      formData.preselectOptions?.options
                    );
                    const newPreselectOptions = {
                      defaultSelection:
                        formData.preselectOptions?.defaultSelection || false,
                      options: {
                        ...cleanedOptions,
                        [modGroupId]: newSelectedOptions,
                      },
                    };
                    setFormData({
                      ...formData,
                      preselectOptions: newPreselectOptions,
                    });
                    if (isEditing) {
                      handleFieldAutoSave(
                        'preselectOptions',
                        newPreselectOptions
                      );
                    }
                  };

                  return (
                    <Box
                      key={modGroupId}
                      sx={{
                        mb: 3,
                        p: 2,
                        border: '1px solid #e0e0e0',
                        borderRadius: 2,
                        bgcolor: '#fafafa',
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: '14px',
                          fontWeight: 600,
                          mb: 2,
                          textTransform: 'uppercase',
                          color: '#666',
                        }}
                      >
                        {getTranslationValue(modGroup.name)}
                      </Typography>

                      {options.map((option) => {
                        const optionId = option.itemId;
                        const isSelected = selectedOptions.some(
                          (opt) => opt._id === optionId
                        );
                        const selectedOption = selectedOptions.find(
                          (opt) => opt._id === optionId
                        );
                        const optionName =
                          getTranslationValue(option.itemData?.name) ||
                          'Option';

                        // Check if this option has nested modifier groups
                        const hasNestedModifiers =
                          option.itemData?.modifierGroupIds?.length > 0;
                        const isExpanded =
                          expandedNestedOptions[`${modGroupId}_${optionId}`];

                        return (
                          <Box key={optionId}>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                py: 1,
                                borderBottom: '1px solid #eee',
                              }}
                            >
                              {isRadioMode ? (
                                /* Radio mode - for min=1 && max=1 (single select) */
                                <FormControlLabel
                                  control={
                                    <Radio
                                      checked={isSelected}
                                      onClick={() =>
                                        handleRadioSelect(optionId)
                                      }
                                      size="small"
                                      disabled={preselectSaving}
                                    />
                                  }
                                  label={
                                    <Typography sx={{ fontSize: '14px' }}>
                                      {optionName}
                                    </Typography>
                                  }
                                />
                              ) : showQuantityInput ? (
                                /* Quantity input mode - for canSelectMultipleOptions=true */
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 2,
                                    flex: 1,
                                  }}
                                >
                                  <Typography
                                    sx={{ fontSize: '14px', flex: 1 }}
                                  >
                                    {optionName}
                                  </Typography>
                                  <TextField
                                    size="small"
                                    type="number"
                                    label="Qty"
                                    value={selectedOption?.count || ''}
                                    disabled={preselectSaving}
                                    onChange={(e) => {
                                      const val = e.target.value;
                                      if (val === '' || parseInt(val) === 0) {
                                        // Remove option when quantity is cleared or 0
                                        const newSelectedOptions =
                                          selectedOptions.filter(
                                            (opt) => opt._id !== optionId
                                          );
                                        const newPreselectOptions = {
                                          ...formData.preselectOptions,
                                          options: {
                                            ...formData.preselectOptions
                                              ?.options,
                                            [modGroupId]: newSelectedOptions,
                                          },
                                        };
                                        setFormData({
                                          ...formData,
                                          preselectOptions: newPreselectOptions,
                                        });
                                        if (isEditing) {
                                          handleFieldAutoSave(
                                            'preselectOptions',
                                            newPreselectOptions
                                          );
                                        }
                                      } else {
                                        // Add or update quantity
                                        if (isSelected) {
                                          handleQuantityChange(
                                            optionId,
                                            parseInt(val)
                                          );
                                        } else {
                                          // Add new option with quantity
                                          const newSelectedOptions = [
                                            ...selectedOptions,
                                            {
                                              _id: optionId,
                                              count: parseInt(val),
                                              sticky: false,
                                            },
                                          ];
                                          const newPreselectOptions = {
                                            ...formData.preselectOptions,
                                            options: {
                                              ...formData.preselectOptions
                                                ?.options,
                                              [modGroupId]: newSelectedOptions,
                                            },
                                          };
                                          setFormData({
                                            ...formData,
                                            preselectOptions:
                                              newPreselectOptions,
                                          });
                                          if (isEditing) {
                                            handleFieldAutoSave(
                                              'preselectOptions',
                                              newPreselectOptions
                                            );
                                          }
                                        }
                                      }
                                    }}
                                    onWheel={(e) => e.target.blur()}
                                    inputProps={{ min: 0 }}
                                    sx={{ width: 80 }}
                                    InputLabelProps={{ shrink: true }}
                                  />
                                </Box>
                              ) : (
                                /* Checkbox mode - for canSelectMultipleOptions=false */
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={isSelected}
                                      onChange={() =>
                                        handleOptionToggle(optionId, false)
                                      }
                                      size="small"
                                      disabled={preselectSaving}
                                    />
                                  }
                                  label={
                                    <Typography sx={{ fontSize: '14px' }}>
                                      {optionName}
                                    </Typography>
                                  }
                                />
                              )}

                              {/* Expand button for nested modifiers - always reserve space for consistency */}
                              <Box
                                sx={{
                                  width: 32,
                                  minWidth: 32,
                                  display: 'flex',
                                  justifyContent: 'center',
                                }}
                              >
                                {hasNestedModifiers && (
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      setExpandedNestedOptions((prev) => ({
                                        ...prev,
                                        [`${modGroupId}_${optionId}`]:
                                          !prev[`${modGroupId}_${optionId}`],
                                      }))
                                    }
                                  >
                                    {isExpanded ? (
                                      <ExpandLessIcon fontSize="small" />
                                    ) : (
                                      <ExpandMoreIcon fontSize="small" />
                                    )}
                                  </IconButton>
                                )}
                              </Box>
                            </Box>

                            {/* Nested Modifier Groups - using recursive component */}
                            {hasNestedModifiers && isExpanded && (
                              <NestedModifierSelector
                                optionItem={option.itemData}
                                menuMap={menuMap}
                                depth={1}
                                expandedNestedOptions={expandedNestedOptions}
                                setExpandedNestedOptions={
                                  setExpandedNestedOptions
                                }
                                parentKey={`${modGroupId}_${optionId}`}
                                preselectSaving={preselectSaving}
                                setPreselectSaving={setPreselectSaving}
                                parentPreselectOptions={
                                  formData.preselectOptions
                                }
                                onNestedPreselectChange={(
                                  compositeKey,
                                  newSelectedOptions
                                ) => {
                                  const newPreselectOptions = {
                                    ...formData.preselectOptions,
                                    defaultSelection: true,
                                    options: {
                                      ...formData.preselectOptions?.options,
                                      [compositeKey]: newSelectedOptions,
                                    },
                                  };
                                  setFormData({
                                    ...formData,
                                    preselectOptions: newPreselectOptions,
                                  });
                                  if (isEditing) {
                                    handleFieldAutoSave(
                                      'preselectOptions',
                                      newPreselectOptions
                                    );
                                  }
                                }}
                              />
                            )}
                          </Box>
                        );
                      })}

                      {selectedOptions.length > 0 && (
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={isStickyEnabled}
                              onChange={handleStickyToggle}
                              size="small"
                            />
                          }
                          label={
                            <Box>
                              <Typography sx={{ fontSize: '13px' }}>
                                Freeze Selection
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: '11px',
                                  color: '#9E9EAD',
                                }}
                              >
                                Lock pre-selected options so customers cannot
                                change them
                              </Typography>
                            </Box>
                          }
                          sx={{ mt: 2 }}
                        />
                      )}
                    </Box>
                  );
                })}
              </Box>
            )}
          </AccordionDetails>
        </Accordion>
      )}

      {/* Inventory Accordion */}
      {isEditing && (
        <Accordion
          expanded={isInventoryExpanded}
          onChange={(_, expanded) => setIsInventoryExpanded(expanded)}
          TransitionProps={{ unmountOnExit: true }}
          sx={{
            mb: 3,
            boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
            '&:before': { display: 'none' },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{ px: 4, py: 2 }}
          >
            <Box>
              <Typography
                sx={{
                  fontSize: '18px',
                  fontWeight: 600,
                  color: '#141414',
                }}
              >
                Inventory
              </Typography>
              <Typography
                sx={{
                  fontSize: '13px',
                  color: '#9E9EAD',
                  mt: 0.5,
                }}
              >
                Manage stock, SKU and component items for this item
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 4, pb: 4, pt: 0 }}>
            {isInventoryExpanded && (
              <ItemInventory key={currentItem?._id} item={currentItem} />
            )}
          </AccordionDetails>
        </Accordion>
      )}

      {/* Additional Options Accordion */}
      <Accordion
        sx={{
          boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
          '&:before': { display: 'none' },
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 4, py: 2 }}>
          <Box>
            <Typography
              sx={{
                fontSize: '18px',
                fontWeight: 600,
                color: '#141414',
              }}
            >
              Advanced Options
            </Typography>
            <Typography
              sx={{
                fontSize: '13px',
                color: '#9E9EAD',
                mt: 0.5,
              }}
            >
              Additional settings and kitchen instructions
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 4, pb: 4, pt: 2 }}>
          <Box
            sx={{
              mb: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <Typography
              sx={{
                fontSize: '14px',
                fontWeight: 500,
                width: 160,
                flexShrink: 0,
              }}
            >
              Hide Name on Order
            </Typography>
            <Switch
              checked={formData.hideNameOnOrder || false}
              onChange={(e) => {
                const newValue = e.target.checked;
                setFormData({ ...formData, hideNameOnOrder: newValue });
                if (isEditing) {
                  handleFieldAutoSave('hideNameOnOrder', newValue);
                }
              }}
            />
            <FieldSavingIndicator status={fieldSavingStatus.hideNameOnOrder} />
          </Box>

          <Box
            sx={{
              mb: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <Typography
              sx={{
                fontSize: '14px',
                fontWeight: 500,
                width: 160,
                flexShrink: 0,
              }}
            >
              Weighted Item
            </Typography>
            <Switch
              checked={formData.isWeighted || false}
              onChange={(e) => {
                const newValue = e.target.checked;
                setFormData({ ...formData, isWeighted: newValue });
                if (isEditing) {
                  handleFieldAutoSave('isWeighted', newValue);
                }
              }}
            />
            <FieldSavingIndicator status={fieldSavingStatus.isWeighted} />
          </Box>

          <Box
            sx={{
              mb: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <Typography
              sx={{
                fontSize: '14px',
                fontWeight: 500,
                width: 160,
                flexShrink: 0,
              }}
            >
              Nested Layout
            </Typography>
            <FormControl size="small" sx={{ width: 200 }}>
              <SearchableSelect
                value={formData.showNestedLayout || 'default'}
                onChange={(e) => {
                  const newValue = e.target.value;
                  setFormData({ ...formData, showNestedLayout: newValue });
                  if (isEditing) {
                    handleFieldAutoSave('showNestedLayout', newValue);
                  }
                }}
              >
                <MenuItem value="default">Default</MenuItem>
                <MenuItem value="popup">Popup</MenuItem>
              </SearchableSelect>
            </FormControl>
            <FieldSavingIndicator status={fieldSavingStatus.showNestedLayout} />
          </Box>

          {formData.priceType === 'multiplierApplyingToParentPrice' && (
            <Box
              sx={{
                mb: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: 500,
                  width: 160,
                  flexShrink: 0,
                }}
              >
                In-Order View Layout
              </Typography>
              <FormControl size="small" sx={{ width: 200 }}>
                <SearchableSelect
                  value={formData.inOrderViewLayout || ''}
                  displayEmpty
                  renderValue={(selected) => {
                    const option = IN_ORDER_VIEW_LAYOUT_OPTIONS.find(
                      (o) => o.value === selected
                    );
                    return option?.label || 'None';
                  }}
                  onChange={(e) => {
                    const newValue = e.target.value || null;
                    setFormData({ ...formData, inOrderViewLayout: newValue });
                    if (isEditing) {
                      handleFieldAutoSave('inOrderViewLayout', newValue);
                    }
                  }}
                >
                  {IN_ORDER_VIEW_LAYOUT_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </SearchableSelect>
              </FormControl>
              <FieldSavingIndicator
                status={fieldSavingStatus.inOrderViewLayout}
              />
            </Box>
          )}

          <Box
            sx={{ mb: 2, display: 'flex', alignItems: 'flex-start', gap: 2 }}
          >
            <Box sx={{ flex: 1 }}>
              <TranslationInput
                label="Kitchen Instructions"
                value={formData.kitchenInstruction || { en: '', es: '' }}
                onChange={(value) =>
                  setFormData({ ...formData, kitchenInstruction: value })
                }
                onBlur={(value) =>
                  handleFieldAutoSave('kitchenInstruction', value)
                }
                multiline
                rows={3}
              />
            </Box>
            <Box sx={{ minWidth: 24, mt: 1 }}>
              <FieldSavingIndicator
                status={fieldSavingStatus.kitchenInstruction}
              />
            </Box>
          </Box>

          <Box
            sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'flex-start' }}
          >
            <TextField
              label="Min Quantity"
              type="number"
              value={formData.minAllowedQuantity ?? ''}
              onChange={(e) => {
                const value = e.target.value;
                setFormData({
                  ...formData,
                  minAllowedQuantity:
                    value === '' ? null : parseInt(value) || null,
                });
              }}
              onBlur={(e) => {
                const value = e.target.value;
                handleFieldAutoSave(
                  'minAllowedQuantity',
                  value === '' ? null : parseInt(value) || null
                );
              }}
              onKeyDown={(e) => handleNumericKeyDown(e, false)}
              onWheel={(e) => e.target.blur()}
              fullWidth
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: '1' }}
              sx={{
                flex: 1,
                '& .MuiInputBase-root': {
                  fontSize: '14px',
                },
              }}
            />
            <Box sx={{ minWidth: 24, mt: 1 }}>
              <FieldSavingIndicator
                status={fieldSavingStatus.minAllowedQuantity}
              />
            </Box>

            <TextField
              label="Max Quantity"
              type="number"
              value={formData.maxAllowedQuantity ?? ''}
              onChange={(e) => {
                const value = e.target.value;
                setFormData({
                  ...formData,
                  maxAllowedQuantity:
                    value === '' ? null : parseInt(value) || null,
                });
              }}
              onBlur={(e) => {
                const value = e.target.value;
                handleFieldAutoSave(
                  'maxAllowedQuantity',
                  value === '' ? null : parseInt(value) || null
                );
              }}
              onKeyDown={(e) => handleNumericKeyDown(e, false)}
              onWheel={(e) => e.target.blur()}
              fullWidth
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: '1' }}
              sx={{
                flex: 1,
                '& .MuiInputBase-root': {
                  fontSize: '14px',
                },
              }}
            />
            <Box sx={{ minWidth: 24, mt: 1 }}>
              <FieldSavingIndicator
                status={fieldSavingStatus.maxAllowedQuantity}
              />
            </Box>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!formData.enforceQtyStrict}
                    onChange={(e) => {
                      const newValue = e.target.checked;
                      setFormData({ ...formData, enforceQtyStrict: newValue });
                      if (isEditing) {
                        handleFieldAutoSave('enforceQtyStrict', newValue);
                      }
                    }}
                  />
                }
                label="Enforce strict quantities"
                sx={{
                  mr: 0,
                  '& .MuiFormControlLabel-label': { fontSize: '14px' },
                }}
              />
              <FieldSavingIndicator
                status={fieldSavingStatus.enforceQtyStrict}
              />
            </Box>
          </Box>

          {/* Min/Max Total Number of Modifier Groups */}
          <Box
            sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'flex-start' }}
          >
            <TextField
              label="Min Total Modifier Groups"
              type="number"
              value={formData.minTotalNumberOfModifierGroups ?? ''}
              onChange={(e) => {
                const value = e.target.value;
                setFormData({
                  ...formData,
                  minTotalNumberOfModifierGroups:
                    value === '' ? null : parseInt(value),
                });
              }}
              onBlur={(e) => {
                const value = e.target.value;
                handleFieldAutoSave(
                  'minTotalNumberOfModifierGroups',
                  value === '' ? null : parseInt(value)
                );
              }}
              onKeyDown={(e) => handleNumericKeyDown(e, false)}
              onWheel={(e) => e.target.blur()}
              fullWidth
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: '0' }}
              sx={{
                flex: 1,
                '& .MuiInputBase-root': {
                  fontSize: '14px',
                },
              }}
            />
            <Box sx={{ minWidth: 24, mt: 1 }}>
              <FieldSavingIndicator
                status={fieldSavingStatus.minTotalNumberOfModifierGroups}
              />
            </Box>

            <TextField
              label="Max Total Modifier Groups"
              type="number"
              value={formData.maxTotalNumberOfModifierGroups ?? ''}
              onChange={(e) => {
                const value = e.target.value;
                setFormData({
                  ...formData,
                  maxTotalNumberOfModifierGroups:
                    value === '' ? null : parseInt(value),
                });
              }}
              onBlur={(e) => {
                const value = e.target.value;
                handleFieldAutoSave(
                  'maxTotalNumberOfModifierGroups',
                  value === '' ? null : parseInt(value)
                );
              }}
              onKeyDown={(e) => handleNumericKeyDown(e, false)}
              onWheel={(e) => e.target.blur()}
              fullWidth
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: '0' }}
              sx={{
                flex: 1,
                '& .MuiInputBase-root': {
                  fontSize: '14px',
                },
              }}
            />
            <Box sx={{ minWidth: 24, mt: 1 }}>
              <FieldSavingIndicator
                status={fieldSavingStatus.maxTotalNumberOfModifierGroups}
              />
            </Box>
          </Box>

          {/* Min/Max Total Number of Modifier Items */}
          <Box
            sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'flex-start' }}
          >
            <TextField
              label="Min Total Modifier Items"
              type="number"
              value={formData.minTotalNumberOfModifierItems ?? ''}
              onChange={(e) => {
                const value = e.target.value;
                setFormData({
                  ...formData,
                  minTotalNumberOfModifierItems:
                    value === '' ? null : parseInt(value),
                });
              }}
              onBlur={(e) => {
                const value = e.target.value;
                handleFieldAutoSave(
                  'minTotalNumberOfModifierItems',
                  value === '' ? null : parseInt(value)
                );
              }}
              onKeyDown={(e) => handleNumericKeyDown(e, false)}
              onWheel={(e) => e.target.blur()}
              fullWidth
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: '0' }}
              sx={{
                flex: 1,
                '& .MuiInputBase-root': {
                  fontSize: '14px',
                },
              }}
            />
            <Box sx={{ minWidth: 24, mt: 1 }}>
              <FieldSavingIndicator
                status={fieldSavingStatus.minTotalNumberOfModifierItems}
              />
            </Box>

            <TextField
              label="Max Total Modifier Items"
              type="number"
              value={formData.maxTotalNumberOfModifierItems ?? ''}
              onChange={(e) => {
                const value = e.target.value;
                setFormData({
                  ...formData,
                  maxTotalNumberOfModifierItems:
                    value === '' ? null : parseInt(value),
                });
              }}
              onBlur={(e) => {
                const value = e.target.value;
                handleFieldAutoSave(
                  'maxTotalNumberOfModifierItems',
                  value === '' ? null : parseInt(value)
                );
              }}
              onKeyDown={(e) => handleNumericKeyDown(e, false)}
              onWheel={(e) => e.target.blur()}
              fullWidth
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: '0' }}
              sx={{
                flex: 1,
                '& .MuiInputBase-root': {
                  fontSize: '14px',
                },
              }}
            />
            <Box sx={{ minWidth: 24, mt: 1 }}>
              <FieldSavingIndicator
                status={fieldSavingStatus.maxTotalNumberOfModifierItems}
              />
            </Box>
          </Box>

          {/* Printer Tags */}
          <Box
            sx={{ mb: 2, display: 'flex', alignItems: 'flex-start', gap: 2 }}
          >
            <FormControl fullWidth size="small">
              <InputLabel id="printer-tags-label" sx={{ background: 'white' }}>
                Printer Tags
              </InputLabel>
              <SearchableSelect
                labelId="printer-tags-label"
                multiple
                value={formData.printerTags || []}
                onChange={(e) => {
                  let value = e.target.value;
                  // Handle "No Tag" selection - clear all tags
                  if (value.includes('__no_tag__')) {
                    value = [];
                  }
                  setFormData({ ...formData, printerTags: value });
                  if (isEditing) {
                    handleFieldAutoSave('printerTags', value);
                  }
                }}
                input={<OutlinedInput label="Printer Tags" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.length === 0 ? (
                      <Typography
                        sx={{ color: 'text.secondary', fontSize: '14px' }}
                      >
                        No Tag
                      </Typography>
                    ) : (
                      <>
                        {selected.slice(0, 5).map((val) => {
                          const found = printerTagsList?.find(
                            (item) => item?.tagName === val
                          );
                          return (
                            <Chip
                              key={val}
                              sx={{
                                height: '26px',
                                fontWeight: 500,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                maxWidth:
                                  found?.tagName?.length > 10
                                    ? '110px'
                                    : 'inherit',
                              }}
                              label={found?.tagName || val}
                              onDelete={(event) => {
                                event.stopPropagation();
                                const updatedValue = (
                                  formData.printerTags || []
                                ).filter((item) => item !== val);
                                setFormData({
                                  ...formData,
                                  printerTags: updatedValue,
                                });
                                if (isEditing) {
                                  handleFieldAutoSave(
                                    'printerTags',
                                    updatedValue
                                  );
                                }
                              }}
                              onMouseDown={(event) => {
                                event.stopPropagation();
                              }}
                              size="small"
                            />
                          );
                        })}
                        {selected.length > 5 && (
                          <Chip
                            sx={{ height: '26px', fontWeight: 500 }}
                            label={`+${selected.length - 5} more`}
                            size="small"
                          />
                        )}
                      </>
                    )}
                  </Box>
                )}
                sx={{
                  '& .MuiSelect-select': {
                    minHeight: '28px',
                  },
                }}
              >
                <MenuItem value="__no_tag__">
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    width="100%"
                  >
                    <ListItemText
                      disableTypography
                      primary={
                        <Typography
                          sx={{ fontStyle: 'italic', color: 'text.secondary' }}
                        >
                          No Tag
                        </Typography>
                      }
                    />
                  </Box>
                </MenuItem>
                {printerTagsList?.map((tag) => (
                  <MenuItem key={tag._id || tag.tagName} value={tag.tagName}>
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      width="100%"
                    >
                      <ListItemText
                        disableTypography
                        primary={<Typography>{tag.tagName}</Typography>}
                      />
                      {(formData.printerTags || []).includes(tag.tagName) && (
                        <CheckIcon
                          fontSize="small"
                          sx={{ color: 'rgba(29, 25, 43, 1)' }}
                        />
                      )}
                    </Box>
                  </MenuItem>
                ))}
              </SearchableSelect>
            </FormControl>
            <Box sx={{ minWidth: 24, mt: 1 }}>
              <FieldSavingIndicator status={fieldSavingStatus.printerTags} />
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Promotions Section */}
      {isEditing && promotionsEnabled && (
        <Card sx={{ mt: 3, boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)' }}>
          <CardContent sx={{ p: 2 }}>
            <Typography sx={{ fontWeight: '400', fontSize: '18px', mb: 2 }}>
              Promotions
            </Typography>
            <PromotionTab
              lockedProductIds={[item.uuid]}
              lockedProductName={getTranslationValue(item?.name)}
            />
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default ItemEditor;
